import React, { useState } from 'react';
import { Modal } from '../../../components/Modal';
import { ModalFooter } from '../../../components/ModalFooter';
import { SubmitButton } from '../../../components/SubmitButton';
import { StepWizard } from '../../../components/StepWizard';
import { useCreateCollection } from '../../../hooks/useCollections';
import { navigateToAngular } from '../../../services/navigate';
import { onAngularEvent } from '../../../services/angular-bridge';
import { notifySuccess, notifyError } from '../../../services/notify';
import { useIsInternalUser } from '../../../hooks/useIsInternalUser';
import { isOrganizationProject, type ProjectDetail } from '../../../types/project';
import type { CreateCollectionBody } from '../../../types/collection';
import {
  Step1MetadataForm,
  defaultMetadata,
  type CollectionMetadata,
  type MetadataErrors,
} from './Step1MetadataForm';
import {
  Step2SourceType,
  defaultSourceState,
  getSourceConfig,
  isSourceValid,
  type SourceState,
} from './Step2SourceType';
import { ImapCredentialsForm } from './sources/ImapCredentialsForm';
import { useProcessCollection } from './hooks/useProcessCollection';
import { useVerifyImap } from './hooks/useVerifyImap';
import { useValidateForensicImage } from './hooks/useValidateForensicImage';

interface Props {
  project: ProjectDetail;
  onClose: () => void;
}

// Step 1 → 2 advance gate. Terms acceptance is checked separately at final save.
function validateMetadata(metadata: CollectionMetadata): MetadataErrors {
  const errors: MetadataErrors = {};
  if (!metadata.custodianName.trim()) errors.custodianName = 'Custodian is required';
  if (!metadata.name.trim()) errors.name = 'Name is required';
  else if (metadata.name.length > 255) errors.name = 'Name must be less than 255 characters';
  return errors;
}

function buildCollectionBody(projectId: string, metadata: CollectionMetadata): CreateCollectionBody {
  return {
    projectId,
    custodian: metadata.custodianName,
    name: metadata.name,
    description: metadata.description,
    ritm: metadata.ritm,
    evidence: metadata.evidence,
    release: metadata.release,
    extractFromContainers: metadata.extractFromContainers,
    extractAttachmentAndEmbeddedDocumentsFromEmails: metadata.extractAttachmentAndEmbeddedDocumentsFromEmails,
    extractImagesFromEmails: metadata.extractImagesFromEmails,
    extractDocumentsFromFiles: metadata.extractDocumentsFromFiles,
    extractImagesFromFiles: metadata.extractImagesFromFiles,
    imagePresentationsWithNotes: metadata.imagePresentationsWithNotes,
    cancelTranscription: metadata.cancelTranscription,
    cancelExtractEntities: metadata.cancelExtractEntities,
    cancelPdf: metadata.cancelPdf,
    cancelOcr: metadata.cancelOcr,
    piiDetectionMode: metadata.piiDetectionMode,
  };
}

export function NewCollectionModal({ project, onClose }: Props) {
  const projectId = project.id;
  const isTrial = project.status === 'trial';

  const [step, setStep] = useState(1);
  const [dirty, setDirty] = useState(false);
  const [metadata, setMetadata] = useState<CollectionMetadata>(() => defaultMetadata(project.status));
  const [sourceState, setSourceState] = useState<SourceState>(defaultSourceState);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [upload, setUpload] = useState({ progress: 0, loaded: 0, total: 0 });

  const isInternalUser = useIsInternalUser();
  const createCollection = useCreateCollection(projectId);
  const processCollection = useProcessCollection();
  const verifyImap = useVerifyImap(projectId);
  const validateForensic = useValidateForensicImage();

  const metadataErrors = dirty ? validateMetadata(metadata) : {};
  const metadataValid = Object.keys(validateMetadata(metadata)).length === 0;
  const termsAccepted = isTrial || metadata.acceptTermsAndConditions;
  const sourceValid = isSourceValid(sourceState) && termsAccepted;
  const isBusy = saving || verifying;

  // IMAP is a 3-step flow (provider select → credentials); every other source is 2 steps.
  const isImap = sourceState.sourceType === 'imap';
  const totalSteps = isImap ? 3 : 2;
  const isFinalStep = step === totalSteps;
  const providerSelected = sourceState.imapConfig.provider !== '';

  const safeClose = isBusy ? () => {} : onClose;

  function handleNext() {
    setDirty(true);
    if (!metadataValid) return;
    setStep(2);
  }

  async function handleSave() {
    setSaving(true);
    const body = buildCollectionBody(projectId, metadata);
    const sourceConfig = getSourceConfig(sourceState);

    if (sourceState.sourceType === 'forensicimage') {
      notifySuccess({
        title: 'Image validating',
        description: `${sourceState.forensicImageType} image is being validated, you will be notified when processing begins.`,
        timeout: 5000,
      });
      validateForensic.mutate(
        { projectId, sourceConfig, collectionBody: body },
        { onError: () => setSaving(false) },
      );
      const unsubscribe = onAngularEvent(
        'FeedService.ForensicImageValidationComplete',
        (message: { status: string; collectionId?: string }) => {
          unsubscribe();
          if (message.status === 'Valid' && message.collectionId) {
            notifySuccess({
              title: 'Forensic image is valid, processing started.',
              description: '',
              timeout: 5000,
            });
            onClose();
            navigateToAngular(`/project/${projectId}/collection/${message.collectionId}`);
          } else {
            setSaving(false);
            notifyError({
              title: 'Forensic image is invalid.',
              description: `The image does not conform to the ${sourceState.forensicImageType} format.`,
              timeout: 5000,
            });
          }
        },
      );
      onClose();
      return;
    }

    if (sourceState.sourceType === 'imap') {
      setVerifying(true);
      verifyImap.mutate(sourceConfig, {
        onSuccess: () => {
          setVerifying(false);
          saveAndProcess(body, sourceConfig);
        },
        onError: () => {
          setVerifying(false);
          setSaving(false);
        },
      });
      return;
    }

    saveAndProcess(body, sourceConfig);
  }

  function saveAndProcess(body: CreateCollectionBody, sourceConfig: Record<string, any>) {
    createCollection.mutate(body, {
      onSuccess: collection => {
        processCollection.mutate(
          {
            projectId,
            collectionId: collection.id,
            sourceConfig,
            file: sourceState.selectedFile ?? undefined,
            onProgress: (loaded, total, percent) => setUpload({ loaded, total, progress: percent }),
          },
          {
            onSuccess: () => {
              notifySuccess({ title: 'Processing started', description: '', timeout: 5000 });
              onClose();
              navigateToAngular(`/project/${projectId}/collection/${collection.id}`);
            },
            onError: () => setSaving(false),
          },
        );
      },
      onError: () => setSaving(false),
    });
  }

  return (
    <Modal
      title={<>Add Collection <small>Step {step} of {totalSteps}</small></>}
      icon="far fa-hdd"
      onClose={safeClose}
      large
      footer={
        step === 1 ? (
          <ModalFooter
            onClose={safeClose}
            onNext={handleNext}
            nextLabel="Next →"
          />
        ) : !isFinalStep ? (
          // IMAP step 2: choose a provider before continuing to credentials.
          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: 6 }}>
            <button type="button" className="btn btn-default" onClick={() => setStep(step - 1)} disabled={isBusy}>
              ← Back
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(step + 1)} disabled={!providerSelected || isBusy}>
              Next →
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', gap: 12 }}>
            <div>
              {!isTrial && (
                <div className="checkbox checkbox-primary" style={{ margin: 0 }}>
                  <input
                    id="acceptTermsAndConditions"
                    type="checkbox"
                    checked={metadata.acceptTermsAndConditions}
                    onChange={e => setMetadata({ ...metadata, acceptTermsAndConditions: e.target.checked })}
                    disabled={isBusy}
                  />
                  <label htmlFor="acceptTermsAndConditions">
                    I understand this action incurs billable costs and accept all charges associated.
                  </label>
                </div>
              )}
            </div>
            <div style={{ whiteSpace: 'nowrap' }}>
              <button type="button" className="btn btn-default" onClick={() => setStep(step - 1)} disabled={isBusy}>
                ← Back
              </button>{' '}
              <SubmitButton
                pending={isBusy}
                pendingLabel={verifying ? 'Verifying…' : saving ? 'Processing…' : 'Saving…'}
                disabled={!sourceValid}
                onClick={handleSave}
                type="submit"
                className="btn btn-primary"
              >
                Save
              </SubmitButton>
            </div>
          </div>
        )
      }
    >
      <StepWizard currentStep={step}>
        <Step1MetadataForm
          projectId={projectId}
          project={project}
          metadata={metadata}
          onChange={setMetadata}
          errors={metadataErrors}
          isInternalUser={isInternalUser}
          disabled={isBusy}
        />
        <Step2SourceType
          projectId={projectId}
          collectionName={metadata.name}
          isOrganizationProject={isOrganizationProject(project)}
          state={sourceState}
          onChange={s => setSourceState(s)}
          saving={isBusy}
          uploading={saving && sourceState.sourceType === 'upload'}
          uploadProgress={upload.progress}
          uploadBytesLoaded={upload.loaded}
          uploadBytesTotal={upload.total}
        />
        <ImapCredentialsForm
          config={sourceState.imapConfig}
          onChange={cfg => setSourceState({ ...sourceState, imapConfig: cfg })}
          disabled={isBusy}
        />
      </StepWizard>
    </Modal>
  );
}
